import { useCallback, useEffect, useState } from "react";
import { api, parseApiError } from "@/lib/api";
import { PageHeader } from "./AdminLayout";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DataStateRow } from "@/components/ui/data-state";
import { toast } from "sonner";
import { RefreshCcw, ShieldOff } from "lucide-react";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const GRANT_STATUS = {
  active:  { label: "Đang hiệu lực", color: "bg-green-50 text-green-700" },
  expired: { label: "Hết hạn",       color: "bg-stone-100 text-stone-500" },
  revoked: { label: "Thu hồi",       color: "bg-red-50 text-red-600" },
};

function grantStatus(grant) {
  if (!grant.is_active && grant.revoked_at) return "revoked";
  if (!grant.is_active) return "expired";
  if (new Date(grant.expires_at) < new Date()) return "expired";
  return "active";
}

function GrantStatusBadge({ grant }) {
  const key = grantStatus(grant);
  const s = GRANT_STATUS[key];
  return <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${s.color}`}>{s.label}</span>;
}

function shortId(id) {
  return id ? id.slice(0, 8).toUpperCase() : "—";
}

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleDateString("vi-VN"); } catch { return s; }
}

// ---------------------------------------------------------------------------
// Revoke confirmation dialog
// ---------------------------------------------------------------------------
function RevokeDialog({ open, onClose, onDone, grant }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setReason(""); }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/legal/grants/${grant.id}/revoke`, { reason: reason.trim() || null });
      toast.success("Đã thu hồi quyền truy cập tài liệu");
      onDone();
      onClose();
    } catch (err) {
      toast.error(parseApiError(err, "Không thu hồi được").message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Thu hồi quyền truy cập</DialogTitle>
        </DialogHeader>
        {grant && (
          <form onSubmit={submit} className="space-y-3 mt-2">
            <div className="bg-stone-50 rounded p-3 text-sm space-y-1">
              <div><span className="text-stone-500">Người dùng:</span> {grant.user_name}</div>
              <div><span className="text-stone-500">Tài liệu:</span> {grant.file_name}</div>
              <div><span className="text-stone-500">Hết hạn:</span> {fmtDate(grant.expires_at)}</div>
            </div>
            <div>
              <Label>Lý do thu hồi (tuỳ chọn)</Label>
              <Textarea
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Nhập lý do…"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={saving}>Huỷ</Button>
              <Button type="submit" variant="destructive" disabled={saving}
                data-testid="legal-grant-revoke-confirm">
                {saving ? "Đang thu hồi…" : "Xác nhận thu hồi"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Filter tabs
// ---------------------------------------------------------------------------
const FILTER_TABS = [
  { key: "active",  label: "Đang hiệu lực" },
  { key: "expired", label: "Hết hạn" },
  { key: "revoked", label: "Thu hồi" },
  { key: "",        label: "Tất cả" },
];

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function LegalVaultGrants() {
  const [grants, setGrants] = useState([]);
  const [statusFilter, setStatusFilter] = useState("active");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revokeDialog, setRevokeDialog] = useState({ open: false, grant: null });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get("/legal/grants");
      setGrants(r.data || []);
    } catch (e) {
      setError(parseApiError(e, "Không tải được danh sách cấp phát").message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Filter client-side (backend returns all, status derived from fields)
  const filtered = statusFilter
    ? grants.filter((g) => grantStatus(g) === statusFilter)
    : grants;

  return (
    <div data-testid="page-legal-grants">
      <PageHeader
        kicker="Admin • Quản lý cấp tài liệu"
        title="Quản lý cấp tài liệu"
        breadcrumb={["Pháp lý", "Quản lý cấp"]}
        actions={
          <Button type="button" variant="outline" onClick={load} className="rounded-sm">
            <RefreshCcw className="w-4 h-4 mr-1.5" />Tải lại
          </Button>
        }
      />

      <div className="p-6 lg:p-8 space-y-4">
        {/* Filter tabs */}
        <div className="flex gap-2 flex-wrap">
          {FILTER_TABS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setStatusFilter(key)}
              className={`px-3 py-1.5 rounded-sm text-sm border transition-colors ${
                statusFilter === key
                  ? "bg-stone-900 text-white border-stone-900"
                  : "border-stone-200 hover:border-stone-400"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
          {loading && <DataStateRow loading />}
          {error && <div className="p-4 text-red-600 text-sm">{error}</div>}
          {!loading && !error && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-100 text-stone-500 text-xs bg-stone-50">
                  <th className="px-4 py-2 text-left">ID</th>
                  <th className="px-4 py-2 text-left">Người dùng</th>
                  <th className="px-4 py-2 text-left">Pháp nhân</th>
                  <th className="px-4 py-2 text-left">Tài liệu</th>
                  <th className="px-4 py-2 text-left">Ngày cấp</th>
                  <th className="px-4 py-2 text-left">Hết hạn</th>
                  <th className="px-4 py-2 text-left">Trạng thái</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-stone-400">
                      Không có cấp phát nào
                    </td>
                  </tr>
                )}
                {filtered.map((grant) => (
                  <tr key={grant.id} className="border-b border-stone-50 hover:bg-stone-50"
                    data-testid={`legal-grant-row-${grant.id}`}>
                    <td className="px-4 py-3 font-mono text-xs text-stone-400">
                      {shortId(grant.id)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{grant.user_name}</div>
                      <div className="text-xs text-stone-400">{grant.user_email}</div>
                    </td>
                    <td className="px-4 py-3">{grant.entity_name}</td>
                    <td className="px-4 py-3">
                      <div className="truncate max-w-[180px]">{grant.file_name}</div>
                      <div className="text-xs text-stone-400">{grant.document_type_name}</div>
                    </td>
                    <td className="px-4 py-3 text-stone-500 text-xs whitespace-nowrap">
                      {fmtDate(grant.created_at)}
                    </td>
                    <td className="px-4 py-3 text-stone-500 text-xs whitespace-nowrap">
                      {fmtDate(grant.expires_at)}
                    </td>
                    <td className="px-4 py-3">
                      <GrantStatusBadge grant={grant} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      {grantStatus(grant) === "active" && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setRevokeDialog({ open: true, grant })}
                          data-testid={`legal-grant-revoke-${grant.id}`}
                        >
                          <ShieldOff className="w-3.5 h-3.5 mr-1" />Thu hồi
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <RevokeDialog
        open={revokeDialog.open}
        onClose={() => setRevokeDialog({ open: false, grant: null })}
        onDone={load}
        grant={revokeDialog.grant}
      />
    </div>
  );
}
